import { Checkbox } from "components/Atoms/inputs/Checkbox"
import { RadioGroup } from "components/Atoms/inputs/RadioGroup"
import { FilterRangeEndDatePicker, FilterRangeStartDatePicker } from "components/DatePicker/RangeDatePicker"
import CulturesInterfaceDropdown from "components/dropdowns/reactselect/CulturesInterfaceDropdown"
import { DetailSectionTemplate } from "components/MainSectionTemplate/DetailSectionTemplate"
import { emptySubmitChangeFunc } from "components/MainSectionTemplate/emptySubmitChangeFunc"
import { ArtistMultiModalPicker, SelectedArtists } from "components/Picker/ArtistPicker/ArtistMultiModalPicker"
import { ContactMultiModalPicker, SelectedContacts } from "components/Picker/ContactPicker/ContactMultiModalPicker"
import { ProductionMultiModalPicker, SelectedProductions } from "components/Picker/ProductionPicker/ProductionMultiModalPicker"
import moment from "moment"
import React from "react"
import { AppointmentPhaseMultiPicker } from "./components/AppointmentPhaseMultiPicker"
import { AppointmentTypeMultiPicker } from "./components/AppointmentTypeMultiPicker"
import { ItineraryExportFor, ItineraryExportForm, ItineraryExportOptions } from "./model"

interface Props {
  formState: ItineraryExportForm
  options: ItineraryExportOptions
  onFormChange: (form: ItineraryExportForm) => void
  onOptionsChange: (options: ItineraryExportOptions) => void
}

export const ItineraryExportSection = (props: Props) => {
  const onForChange = (value: ItineraryExportFor) => {
    props.onFormChange({
      ...props.formState,
      for: value,
      artists: [],
      contacts: [],
      productions: [],
    })
  }

  const onValueChanged = (key: string, value: any) => {
    if (key === "artist") {
      props.onFormChange({
        ...props.formState,
        artists: value,
      });
    } else if (key === "contact") {
      props.onFormChange({
        ...props.formState,
        contacts: value,
      });
    } else if (key === "production") {
      props.onFormChange({
        ...props.formState,
        productions: value,
      });
    }
  }

  return <DetailSectionTemplate
    isInPageEditable
    onValueChanged={onValueChanged}
    sections={[{
      type: "regular",
      key: "itineraryForm",
      title: RESX.Planning.Itinerary,
      fields: [{
        key: "for",
        label: RESX.Preposition.For,
        render: () => <RadioGroup
          orientation="vertical"
          options={[{
            label: site.currentEnvironment.functionGroups.artist.singular,
            value: "artist"
          }, {
            label: RESX.Contact.resxContact,
            value: "contact"
          }, ...site.currentEnvironment.tour.enabled ? [{
            label: site.currentEnvironment.tour.singular,
            value: "production"
          }] : []]}
          value={props.formState.for}
          onChange={onForChange}
        />
      }, {
        key: "artist",
        label: site.currentEnvironment.functionGroups.artist.singular,
        isEmpty: props.formState.artists.length === 0,
        hidden: props.formState.for !== "artist",
        render: () => <SelectedArtists value={props.formState.artists} />,
        inPageEdit: {
          fullWidth: true,
          disableClickOutside: true,
          render: ({ value, onChange, cancel }) => <div className="main-section-template-fields-padding">
            <ArtistMultiModalPicker
              securityScope="limitByUserAccess"
              inPageEditing={{ cancel }}
              value={value}
              onChange={(v) => {
                onChange(v, true);
              }}
              canCreateNew={false}
              required={false}
              validatorKey="artists"
            />
          </div>,
          initialValue: props.formState.artists,
          type: "custom",
          submitChange: emptySubmitChangeFunc,
        }
      }, {
        key: "contact",
        label: RESX.Contact.resxContact,
        isEmpty: props.formState.contacts.length === 0,
        hidden: props.formState.for !== "contact",
        render: () => <SelectedContacts value={props.formState.contacts} />,
        inPageEdit: {
          fullWidth: true,
          disableClickOutside: true,
          render: ({ value, onChange, cancel }) => <ContactMultiModalPicker
            inPageEditing={{ cancel }}
            value={value}
            onChange={contacts => onChange(contacts, true)}
            canCreateNew={false}
            required={false}
            validatorKey="contacts"
          />,
          initialValue: props.formState.contacts,
          type: "custom",
          submitChange: emptySubmitChangeFunc,
        }
      }, {
        key: "production",
        label: site.currentEnvironment.tour.singular,
        isEmpty: props.formState.productions.length === 0,
        hidden: props.formState.for !== "production",
        render: () => <SelectedProductions value={props.formState.productions} />,
        inPageEdit: {
          fullWidth: true,
          disableClickOutside: true,
          render: ({ value, onChange, cancel }) => <ProductionMultiModalPicker
            inPageEditing={{ cancel }}
            value={value}
            onChange={v => onChange(v, true)}
            canCreateNew={false}
            required={false}
            validatorKey="productions"
          />,
          initialValue: props.formState.productions,
          type: "custom",
          submitChange: emptySubmitChangeFunc,
        }
      }, {
        key: "date",
        label: RESX.DateTime.lblDate,
        render: () => <div className="flex align-center">
          <div className="margin-right">
            <FilterRangeStartDatePicker
              dontUpdateOnInputChange
              minDate={moment(serverReference.constants.appointment.minDate)}
              maxDate={moment(serverReference.constants.appointment.maxDate)}
              value={props.formState.date.start}
              rangeEnd={props.formState.date.end}
              onChange={(date) => props.onFormChange({
                ...props.formState,
                date: {
                  ...props.formState.date,
                  start: date,
                },
              })}
            />
          </div>
          <div className="margin-right">{RESX.DateTime.UntilAndIncluding}</div>
          <FilterRangeEndDatePicker
            dontUpdateOnInputChange
            minDate={moment(serverReference.constants.appointment.minDate)}
            maxDate={moment(serverReference.constants.appointment.maxDate)}
            value={props.formState.date.end}
            rangeStart={props.formState.date.start}
            onChange={(date) => props.onFormChange({
              ...props.formState,
              date: {
                ...props.formState.date,
                end: date,
              },
            })}
          />
        </div>,
      }, {
        key: "status",
        label: RESX.Status.resxStatus,
        render: () => <AppointmentPhaseMultiPicker
          selectedPhases={props.formState.phases}
          onChange={phases => props.onFormChange({
            ...props.formState,
            phases,
          })}
        />
      }, {
        key: "type",
        label: RESX.Appointment.AppointmentType,
        render: () => <AppointmentTypeMultiPicker
          selectedTypes={props.formState.appointmentTypes}
          onChange={types => props.onFormChange({
            ...props.formState
            , appointmentTypes: types,
          })}
        />
      }]
    }, {
      type: "regular",
      key: "itineraryOptions",
      title: RESX.GeneralLabels.Display,
      fields: [{
        key: "include",
        label: RESX.GeneralLabels.Display,
        render: () => <div className="flex-column gap-small">
          <Checkbox
            label={RESX.Financial.Costs}
            checked={props.options.displayCosts}
            onChange={(_, displayCosts) => props.onOptionsChange({
              ...props.options,
              displayCosts,
            })}
          />
          <Checkbox
            label={RESX.Financial.Sales}
            checked={props.options.displaySales}
            onChange={(_, displaySales) => props.onOptionsChange({
              ...props.options,
              displaySales,
            })}
          />
        </div>
      }, {
        key: "language",
        label: RESX.Language.resxLanguage,
        render: () => <CulturesInterfaceDropdown
          value={serverReference.interfaceCultures.find(c => c.value === props.options.culture)}
          onChange={c => props.onOptionsChange({
            ...props.options,
            culture: c.value,
          })}
        />
      }]
    }]}
  />
}